import React, { useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import {
  previewWorkOrderImport,
  commitWorkOrderImport,
  getPlanEnrichedSitesPage,
  getPlanGroups,
  createPlanGroup,
} from "api/ServicePlans";
import { geocodeRows, GeocodeInput } from "helpers/geocodeRows";

/**
 * WorkOrderImportModal — Session 67 (STEP 2 of the WO CSV importer)
 *
 * Entry point for landing a batch of Corrigo work orders from a CSV onto
 * a work_order_fed plan (EMCOR/LCG). The flow mirrors ImportMapDedupModal:
 * a Modal shell, per-row choice cards, a choice map resolved on confirm.
 *
 * Phases:
 *   1. pick   — choose a CSV file; parse it client-side into preview rows.
 *   2. review — POST /import/preview; bucket rows by status. Clean matches
 *               (matched) roll up into one "will import" line and don't
 *               demand attention. duplicate/invalid roll up as skipped.
 *               Only ambiguous + needs_review surface as actionable cards:
 *                 · ambiguous    → radios over the returned candidate LCPs.
 *                 · needs_review → a manual site picker (reuses the plan's
 *                                  existing searchable sites endpoint) OR
 *                                  skip (park). Default: skip.
 *   3. done   — POST /import/commit; show per-row outcome.
 *
 * At 40 rows (and up to 125+) the review list is a WORK QUEUE, not a
 * directory: the operator only touches the rows the matcher couldn't
 * resolve. Everything confidently matched imports without a click.
 *
 * The commit path seeds the batch roster server-side (addMany, idempotent)
 * before create(), so the modal just passes groupId — no separate roster
 * step. A create row MUST carry matchedLcpId; needs_review rows left on
 * "skip" park (they do not import this pass), which is the intended
 * behavior — getting the core WO created matters more than backfilling a
 * site the matcher couldn't place.
 */

// ─── Types (mirror the backend preview/commit contract) ──────────────

type PreviewStatus =
  | "matched"
  | "ambiguous"
  | "needs_review"
  | "duplicate"
  | "invalid";

interface WoImportPreviewRow {
  rowIndex: number;
  externalId?: string;
  name?: string;
  line1?: string;
  city?: string;
  state?: string;
  zip?: string;
  problemDescription?: string;
  priority?: string;
  nteAmount?: number;
  onSiteByAt?: string;
  completeByAt?: string;
  acceptRejectByAt?: string;
  receivedAt?: string;
}

interface WoImportCandidate {
  lcpId: string;
  locationId: string;
  locationName?: string;
  address?: string;
  score: number;
}

interface WoImportRowPreviewResult {
  rowIndex: number;
  externalId?: string;
  status: PreviewStatus;
  matchedLcpId?: string;
  matchedLocationId?: string;
  matchedLocationName?: string;
  matchScore?: number;
  candidates?: WoImportCandidate[];
  invalidReason?: string;
}

interface CommitResult {
  createdWorkOrderIds?: string[];
  duplicates?: number;
  errors?: Array<{ externalId?: string; reason: string }>;
  skipped?: number;
  error?: boolean;
}

interface Props {
  servicePlanId: string;
  /** Optional. When set, imported WOs seed this batch's roster and adopt
   * it. When omitted (an urgent WO outside any batch), the backend raises
   * the WO batch-less or auto-adopts the site's existing batch. */
  groupId?: string;
  /** Human label for the plan/batch, shown in the header. */
  planLabel?: string;
  onCancel: () => void;
  /** Called after a successful commit so the parent can refetch. */
  onCommitted?: (result: CommitResult) => void;
}

// Per-row operator decision for the rows that need one. Clean matches are
// not tracked here — they default to create with their matchedLcpId.
type Decision =
  | { action: "skip" }
  | { action: "create"; matchedLcpId: string; matchedLocationId?: string }
  | { action: "create_site" };

const styles: Record<string, React.CSSProperties> = {
  intro: { fontSize: 13, color: "#5D5E62", marginTop: 0, marginBottom: 16, lineHeight: 1.5 },
  dropZone: {
    border: "1px dashed #C4C8CE",
    borderRadius: 8,
    padding: "28px 16px",
    textAlign: "center",
    color: "#5D5E62",
    fontSize: 13,
    cursor: "pointer",
    background: "#FAFBFC",
  },
  fileName: { fontSize: 13, color: "#111", marginTop: 10, fontWeight: 600 },
  rollup: { fontSize: 12, color: "#374151", marginBottom: 10, padding: "8px 12px", borderRadius: 6 },
  rollupGood: { background: "#ECFDF5", border: "1px solid #A7F3D0" },
  rollupMuted: { background: "#F3F4F6", border: "1px solid #E4E7EB" },
  summaryRow: {
    fontSize: 13,
    color: "#374151",
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: "1px solid #E4E7EB",
  },
  summaryStrong: { fontWeight: 700, color: "#047857" },
  summaryWarn: { fontWeight: 700, color: "#B45309" },
  summaryNewSites: { fontSize: 12, color: "#047857", marginTop: 6, fontWeight: 600 },
  candidateRow: { display: "flex", alignItems: "flex-start", gap: 8 },
  candidateLink: {
    fontSize: 12,
    color: "#2563EB",
    textDecoration: "none",
    padding: "2px 8px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    marginTop: 2,
  },
  batchBox: {
    marginTop: 14,
    padding: "12px 14px",
    border: "1px solid #E4E7EB",
    borderRadius: 8,
    background: "#FBFBFA",
  },
  batchTitle: {
    fontSize: 11,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    fontWeight: 600,
    marginBottom: 8,
  },
  batchSelect: {
    display: "block",
    marginTop: 6,
    padding: "6px 8px",
    fontSize: 13,
    border: "1px solid #D1D5DB",
    borderRadius: 6,
    background: "#fff",
    width: "100%",
    maxWidth: 320,
  },
  batchInput: {
    display: "block",
    marginTop: 6,
    padding: "6px 8px",
    fontSize: 13,
    border: "1px solid #D1D5DB",
    borderRadius: 6,
    width: "100%",
    maxWidth: 320,
  },
  disclosure: {
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    fontSize: 12,
    fontWeight: 600,
    color: "#065F46",
    cursor: "pointer",
  },
  matchedItem: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    padding: "5px 0",
    borderTop: "1px solid #C7EBD8",
  },
  matchedFrom: { fontSize: 12, color: "#374151" },
  matchedArrow: { fontSize: 12, color: "#047857", fontWeight: 600 },
  matchedScore: { fontSize: 11, color: "#6B7280", fontWeight: 400 },
  sectionLabel: {
    fontSize: 11,
    color: "#B0B4BA",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 600,
    margin: "16px 0 8px",
  },
  card: { border: "1px solid #E4E7EB", borderRadius: 8, padding: 12, background: "#FFF", marginBottom: 10 },
  cardTitle: { fontSize: 13, fontWeight: 600, color: "#111", margin: 0 },
  cardSub: { fontSize: 12, color: "#5D5E62", marginTop: 2 },
  radioRow: { display: "flex", flexDirection: "column", gap: 6, marginTop: 8 },
  radioOption: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#111", cursor: "pointer", padding: "3px 0" },
  radioHint: { fontSize: 11, color: "#5D5E62", marginLeft: 24, marginTop: 1 },
  searchInput: {
    width: "100%",
    fontSize: 13,
    padding: "7px 10px",
    border: "1px solid #D1D5DB",
    borderRadius: 6,
    marginTop: 8,
    boxSizing: "border-box",
  },
  pickerList: { display: "flex", flexDirection: "column", gap: 4, marginTop: 6, maxHeight: 180, overflowY: "auto" },
  pickerRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "4px 0", cursor: "pointer" },
  chosenTag: { fontSize: 12, color: "#047857", marginTop: 6, fontWeight: 600 },
  errText: { fontSize: 12, color: "#B91C1C", marginTop: 8 },
  resultLine: { fontSize: 13, color: "#111", padding: "4px 0" },
};

// ─── CSV parsing ─────────────────────────────────────────────────────
// Minimal RFC-4180-ish parser: handles quoted fields with embedded commas
// (rows 18/19 in the EMCOR file) and doubled quotes. Header row maps to
// the preview-row keys. nteAmount is coerced to a number; blanks dropped.

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else field += ch;
  }
  if (field !== "" || row.length) {
    row.push(field);
    if (row.some((c) => c.trim() !== "")) rows.push(row);
  }
  return rows;
}

const NUMERIC_KEYS = new Set(["rowIndex", "nteAmount"]);

function rowsFromCsv(text: string): WoImportPreviewRow[] {
  const grid = parseCsv(text);
  if (grid.length < 2) return [];
  const header = grid[0].map((h) => h.trim());
  return grid.slice(1).map((cells, i) => {
    const obj: any = {};
    header.forEach((key, c) => {
      const raw = (cells[c] ?? "").trim();
      if (raw === "") return;
      if (NUMERIC_KEYS.has(key)) {
        const n = Number(raw.replace(/[^0-9.-]/g, ""));
        if (!Number.isNaN(n)) obj[key] = n;
      } else obj[key] = raw;
    });
    // rowIndex is the join key through preview→commit. Trust the file's
    // column if present; otherwise fall back to position.
    if (obj.rowIndex === undefined) obj.rowIndex = i + 1;
    return obj as WoImportPreviewRow;
  });
}

// ─── Manual site picker (needs_review) ───────────────────────────────
// Reuses getPlanEnrichedSitesPage — the plan's existing paged, searchable
// sites read — so no new backend. Rows are keyed by lcpId across the whole
// plan-sites API; we read lcpId and a best-effort address label defensively
// (the enriched row is assembled in a service we don't pin the shape of).

// Coerce a possibly-object address into display text. The enriched/preview
// rows aren't shape-pinned and `address` can arrive as a JSON object
// ({line1,city,...}) rather than a string — dropping that into JSX renders
// "[object Object]". Strings pass through; objects yield their line1;
// anything else drops.
function addrText(v: any): string | undefined {
  if (typeof v === "string") return v.trim() || undefined;
  if (v && typeof v === "object") {
    const l1 = v.line1 || v.address_line1 || v.street;
    return typeof l1 === "string" ? l1.trim() || undefined : undefined;
  }
  return undefined;
}

// Full address line for distinguishing DUPLICATE candidates — includes the
// postal code, since that's usually the only thing that tells two otherwise
// identical LCPs apart (the operator is told to pick the ZIP'd variant).
function addrHint(v: any): string | undefined {
  if (typeof v === "string") return v.trim() || undefined;
  if (v && typeof v === "object") {
    const parts = [v.line1, v.city, v.state, v.postalCode].filter(
      (p) => typeof p === "string" && p.trim() && p.trim().toLowerCase() !== "null",
    );
    return parts.length ? parts.join(", ") : undefined;
  }
  return undefined;
}

function siteLabel(item: any): string {
  const name =
    addrText(item?.locationName) || addrText(item?.name) || addrText(item?.location?.name);
  const line1 =
    addrText(item?.line1) || addrText(item?.location?.line1) || addrText(item?.address);
  const city = addrText(item?.city) || addrText(item?.location?.city);
  const bits = [name, line1, city].filter(Boolean);
  return bits.length ? bits.join(" · ") : item?.lcpId || "Unnamed site";
}

const ManualSitePicker: React.FC<{
  servicePlanId: string;
  chosen?: { matchedLcpId: string; label: string };
  onPick: (lcpId: string, locationId: string | undefined, label: string) => void;
}> = ({ servicePlanId, chosen, onPick }) => {
  const [term, setTerm] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef<any>(null);

  const runSearch = (value: string) => {
    setTerm(value);
    if (debounce.current) clearTimeout(debounce.current);
    if (value.trim().length < 2) {
      setItems([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      const res: any = await getPlanEnrichedSitesPage({
        data: { id: servicePlanId, search: value.trim(), page: 1, pageSize: 20 },
      });
      setItems(Array.isArray(res?.items) ? res.items : []);
      setLoading(false);
    }, 250);
  };

  return (
    <div>
      <input
        style={styles.searchInput}
        placeholder="Search this plan's sites by address or name…"
        value={term}
        onChange={(e) => runSearch(e.target.value)}
      />
      {chosen && <div style={styles.chosenTag}>Will import to: {chosen.label}</div>}
      {loading && <div style={{ fontSize: 12, color: "#5D5E62", marginTop: 6 }}>Searching…</div>}
      {!loading && term.trim().length >= 2 && items.length === 0 && (
        <div style={{ fontSize: 12, color: "#5D5E62", marginTop: 6 }}>
          No matching sites in this plan.
        </div>
      )}
      <div style={styles.pickerList}>
        {items.map((it) => {
          const label = siteLabel(it);
          return (
            <label key={it.lcpId} style={styles.pickerRow}>
              <input
                type="radio"
                name="picker"
                checked={chosen?.matchedLcpId === it.lcpId}
                onChange={() => onPick(it.lcpId, it.locationId, label)}
              />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ─── Modal ───────────────────────────────────────────────────────────

const WorkOrderImportModal: React.FC<Props> = ({
  servicePlanId,
  groupId,
  planLabel,
  onCancel,
  onCommitted,
}) => {
  const [phase, setPhase] = useState<"pick" | "review" | "done">("pick");
  const [fileName, setFileName] = useState<string>("");
  const [parsedRows, setParsedRows] = useState<WoImportPreviewRow[]>([]);
  const [preview, setPreview] = useState<WoImportRowPreviewResult[]>([]);
  const [decisions, setDecisions] = useState<Record<number, Decision>>({});
  const [pickerLabels, setPickerLabels] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [result, setResult] = useState<CommitResult | null>(null);
  // Matched rows roll up into a count by default (a 125-row import
  // shouldn't force a scroll past every good row). Expandable so an
  // operator can VERIFY what matched to what before committing.
  const [showMatched, setShowMatched] = useState(false);
  // Batch control (bottom of review). "keep" = send no groupId; the
  // backend then auto-adopts each site's existing open-batch commitment,
  // or leaves the WO batch-less. "existing"/"new" = an explicit override
  // applied to the whole import. Defaults to the page-scoped batch when
  // the modal was opened from a scoped batch, else "keep".
  const [batchMode, setBatchMode] = useState<"keep" | "existing" | "new">(
    groupId ? "existing" : "keep",
  );
  const [batchList, setBatchList] = useState<any[]>([]);
  const [chosenBatchId, setChosenBatchId] = useState<string>(groupId || "");
  const [newBatchName, setNewBatchName] = useState<string>("");
  const [geoProgress, setGeoProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  const fileInput = useRef<HTMLInputElement>(null);

  const rowsByIndex = useMemo(() => {
    const m: Record<number, WoImportPreviewRow> = {};
    for (const r of parsedRows) m[r.rowIndex] = r;
    return m;
  }, [parsedRows]);

  const buckets = useMemo(() => {
    const b: Record<PreviewStatus, WoImportRowPreviewResult[]> = {
      matched: [],
      ambiguous: [],
      needs_review: [],
      duplicate: [],
      invalid: [],
    };
    for (const r of preview) {
      if (b[r.status]) b[r.status].push(r);
    }
    return b;
  }, [preview]);

  const onFileChosen = async (file: File) => {
    setErrorMsg("");
    const text = await file.text();
    const rows = rowsFromCsv(text);
    if (!rows.length) {
      setErrorMsg("No rows found in that file. Check it has a header line and at least one row.");
      return;
    }
    setFileName(file.name);
    setParsedRows(rows);
    setBusy(true);
    const res: any = await previewWorkOrderImport({ data: { servicePlanId, rows } });
    setBusy(false);
    if (res?.error) {
      setErrorMsg("Preview failed. The plan may not accept imports, or the API is unreachable.");
      return;
    }
    const previewRows: WoImportRowPreviewResult[] = Array.isArray(res?.rows) ? res.rows : [];
    setPreview(previewRows);
    // Seed decisions: ambiguous + needs_review default to skip. The
    // operator must deliberately resolve them; leaving one as skip parks it.
    const seed: Record<number, Decision> = {};
    for (const r of previewRows) {
      if (r.status === "ambiguous" || r.status === "needs_review") {
        seed[r.rowIndex] = { action: "skip" };
      }
    }
    setDecisions(seed);
    // Load the plan's batches for the batch control at the end of review.
    // Non-blocking — an empty list just means "keep" and "new" are the
    // only usable options.
    try {
      const groups = await getPlanGroups({ data: { planId: servicePlanId } });
      setBatchList(Array.isArray(groups) ? groups : []);
    } catch {
      setBatchList([]);
    }
    setPhase("review");
  };

  const chooseCandidate = (rowIndex: number, cand: WoImportCandidate) => {
    setDecisions((prev) => ({
      ...prev,
      [rowIndex]: { action: "create", matchedLcpId: cand.lcpId, matchedLocationId: cand.locationId },
    }));
  };

  const skipRow = (rowIndex: number) => {
    setDecisions((prev) => ({ ...prev, [rowIndex]: { action: "skip" } }));
    setPickerLabels((prev) => {
      const next = { ...prev };
      delete next[rowIndex];
      return next;
    });
  };

  const pickManual = (
    rowIndex: number,
    lcpId: string,
    locationId: string | undefined,
    label: string,
  ) => {
    setDecisions((prev) => ({
      ...prev,
      [rowIndex]: { action: "create", matchedLcpId: lcpId, matchedLocationId: locationId },
    }));
    setPickerLabels((prev) => ({ ...prev, [rowIndex]: label }));
  };

  // Operator chose to create a brand-new site from this row's address.
  // Clears any manual-pick label — the two choices are mutually exclusive.
  const createSiteRow = (rowIndex: number) => {
    setDecisions((prev) => ({ ...prev, [rowIndex]: { action: "create_site" } }));
    setPickerLabels((prev) => {
      const next = { ...prev };
      delete next[rowIndex];
      return next;
    });
  };

  // How many rows will actually create if committed now.
  const createCount = useMemo(() => {
    let n = buckets.matched.length;
    for (const r of [...buckets.ambiguous, ...buckets.needs_review]) {
      const a = decisions[r.rowIndex]?.action;
      if (a === "create" || a === "create_site") n++;
    }
    return n;
  }, [buckets, decisions]);

  // How many rows will create a brand-new site (subset of createCount) —
  // surfaced in the summary so the operator sees new sites before commit.
  const createSiteCount = useMemo(() => {
    let n = 0;
    for (const r of buckets.needs_review) {
      if (decisions[r.rowIndex]?.action === "create_site") n++;
    }
    return n;
  }, [buckets, decisions]);

  const commit = async () => {
    setErrorMsg("");
    setBusy(true);

    // 1. Resolve the batch for this import. "keep" -> undefined (backend
    //    auto-adopts each site's existing commitment). "existing" -> the
    //    chosen batch. "new" -> create it first, then import into it.
    let resolvedGroupId: string | undefined;
    if (batchMode === "existing") {
      resolvedGroupId = chosenBatchId || undefined;
    } else if (batchMode === "new") {
      const name = newBatchName.trim();
      if (!name) {
        setBusy(false);
        setErrorMsg("Name the new batch, or choose a different batch option.");
        return;
      }
      const created: any = await createPlanGroup({
        data: { planId: servicePlanId },
        create: { name },
      });
      if (!created?.id) {
        setBusy(false);
        setErrorMsg("Couldn't create the batch. Nothing was imported — safe to retry.");
        return;
      }
      resolvedGroupId = created.id;
    }

    // 2. Geocode the create_site rows before commit (the backend needs
    //    coordinates to create a Location). Same helper the sites-import
    //    uses. Rows that fail geocoding fall back to skip.
    const siteRows = buckets.needs_review.filter(
      (r) => decisions[r.rowIndex]?.action === "create_site",
    );
    let geoByRow: Record<number, { latitude: number | null; longitude: number | null }> = {};
    if (siteRows.length) {
      setGeoProgress({ done: 0, total: siteRows.length });
      const inputs: GeocodeInput[] = siteRows.map((r) => {
        const src = rowsByIndex[r.rowIndex] || ({} as any);
        return {
          rowIndex: r.rowIndex,
          line1: src.line1 || "",
          city: src.city || "",
          state: src.state || "",
          zip: src.zip || "",
        };
      });
      const geoResults = await geocodeRows(inputs, (done, total) =>
        setGeoProgress({ done, total }),
      );
      geoByRow = Object.fromEntries(
        geoResults.map((g) => [g.rowIndex, { latitude: g.latitude, longitude: g.longitude }]),
      );
      setGeoProgress(null);
    }

    // 3. Assemble commit rows: matched -> create with its resolved LCP;
    //    ambiguous/needs_review -> the operator's decision. A create_site
    //    row carries geocoded coords; if geocoding failed it drops to skip.
    const commitRows: any[] = [];
    let geoFailed = 0;
    for (const r of buckets.matched) {
      const base = rowsByIndex[r.rowIndex] || { rowIndex: r.rowIndex };
      commitRows.push({
        ...base,
        externalId: r.externalId,
        action: "create",
        matchedLcpId: r.matchedLcpId,
        matchedLocationId: r.matchedLocationId,
      });
    }
    for (const r of [...buckets.ambiguous, ...buckets.needs_review]) {
      const d = decisions[r.rowIndex] || { action: "skip" };
      const base = rowsByIndex[r.rowIndex] || { rowIndex: r.rowIndex };
      if (d.action === "create") {
        commitRows.push({
          ...base,
          externalId: r.externalId,
          action: "create",
          matchedLcpId: d.matchedLcpId,
          matchedLocationId: d.matchedLocationId,
        });
      } else if (d.action === "create_site") {
        const geo = geoByRow[r.rowIndex];
        if (!geo || geo.latitude == null || geo.longitude == null) {
          geoFailed++;
          commitRows.push({ ...base, externalId: r.externalId, action: "skip" });
        } else {
          commitRows.push({
            ...base,
            externalId: r.externalId,
            action: "create_site",
            latitude: geo.latitude,
            longitude: geo.longitude,
          });
        }
      } else {
        commitRows.push({ ...base, externalId: r.externalId, action: "skip" });
      }
    }

    const res: any = await commitWorkOrderImport({
      data: { servicePlanId, groupId: resolvedGroupId, rows: commitRows },
    });
    setBusy(false);
    if (res?.error) {
      setErrorMsg("Commit failed. No rows were imported — safe to retry.");
      return;
    }
    if (geoFailed > 0) {
      setErrorMsg(
        `${geoFailed} new-site row${geoFailed === 1 ? "" : "s"} couldn't be located from the address and ${geoFailed === 1 ? "was" : "were"} skipped.`,
      );
    }
    const skipped = commitRows.filter((r) => r.action === "skip").length;
    const out: CommitResult = { ...res, skipped };
    setResult(out);
    setPhase("done");
    onCommitted?.(out);
  };

  // ─── Render ────────────────────────────────────────────────────────

  const headerText =
    phase === "done"
      ? "Import complete"
      : `Import work orders${planLabel ? ` — ${planLabel}` : ""}`;

  return (
    <Modal isOpen={true} toggle={onCancel} size="lg" centered>
      <ModalHeader toggle={onCancel}>{headerText}</ModalHeader>
      <ModalBody>
        {phase === "pick" && (
          <>
            <p style={styles.intro}>
              Upload a CSV of Corrigo work orders. Each row is matched to a
              site in this plan by address; you&apos;ll review anything that
              isn&apos;t a confident match before anything is created.
            </p>
            <div style={styles.dropZone} onClick={() => fileInput.current?.click()} role="button">
              {busy ? "Reading file…" : "Click to choose a .csv file"}
              {fileName && <div style={styles.fileName}>{fileName}</div>}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFileChosen(f);
                e.target.value = "";
              }}
            />
            {errorMsg && <div style={styles.errText}>{errorMsg}</div>}
          </>
        )}

        {phase === "review" && (
          <>
            {/* Counts summary — the shape of the import at a glance, so
             * anyone (not just the person who built the file) can see
             * what's about to happen before committing. Exceptions
             * (ambiguous / needs_review) are surfaced as cards below;
             * matched rolls up here but expands for verification. */}
            <div style={styles.summaryRow}>
              <span style={styles.summaryStrong}>{createCount}</span> will be
              created
              {buckets.ambiguous.length + buckets.needs_review.length > 0 && (
                <>
                  {" · "}
                  <span style={styles.summaryWarn}>
                    {buckets.ambiguous.length + buckets.needs_review.length}
                  </span>{" "}
                  need review
                </>
              )}
              {buckets.duplicate.length + buckets.invalid.length > 0 && (
                <>
                  {" · "}
                  {buckets.duplicate.length + buckets.invalid.length} skipped
                </>
              )}
              {createSiteCount > 0 && (
                <div style={styles.summaryNewSites}>
                  {createSiteCount} new site{createSiteCount === 1 ? "" : "s"} will be
                  created and added to this plan.
                </div>
              )}
            </div>

            {buckets.matched.length > 0 && (
              <div style={{ ...styles.rollup, ...styles.rollupGood }}>
                <button
                  type="button"
                  onClick={() => setShowMatched((v) => !v)}
                  style={styles.disclosure}
                >
                  {showMatched ? "\u25be" : "\u25b8"} {buckets.matched.length}{" "}
                  matched to a site {showMatched ? "(hide)" : "(review)"}
                </button>
                {showMatched && (
                  <div style={{ marginTop: 8 }}>
                    {buckets.matched.map((r) => {
                      const src = rowsByIndex[r.rowIndex];
                      return (
                        <div key={r.rowIndex} style={styles.matchedItem}>
                          <div style={styles.matchedFrom}>
                            {src?.line1 || r.externalId || `Row ${r.rowIndex}`}
                            {src?.city ? `, ${src.city}` : ""}
                            {src?.state ? `, ${src.state}` : ""}
                          </div>
                          <div style={styles.matchedArrow}>
                            {"\u2192 "}
                            {r.matchedLocationName || r.matchedLcpId}
                            {typeof r.matchScore === "number" && (
                              <span style={styles.matchedScore}>
                                {" "}
                                {(r.matchScore * 100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {(buckets.duplicate.length > 0 || buckets.invalid.length > 0) && (
              <div style={{ ...styles.rollup, ...styles.rollupMuted }}>
                {buckets.duplicate.length > 0 && (
                  <div>
                    {buckets.duplicate.length} row{buckets.duplicate.length === 1 ? "" : "s"} already
                    imported (matched an existing work order) — skipped.
                  </div>
                )}
                {buckets.invalid.length > 0 && (
                  <div>
                    {buckets.invalid.length} row{buckets.invalid.length === 1 ? "" : "s"} missing
                    required address fields — skipped.
                  </div>
                )}
              </div>
            )}

            {buckets.ambiguous.length > 0 && (
              <>
                <div style={styles.sectionLabel}>Pick the right site ({buckets.ambiguous.length})</div>
                {buckets.ambiguous.map((r) => {
                  const src = rowsByIndex[r.rowIndex];
                  const chosen = decisions[r.rowIndex];
                  return (
                    <div key={r.rowIndex} style={styles.card}>
                      <div style={styles.cardTitle}>{src?.line1 || r.externalId || `Row ${r.rowIndex}`}</div>
                      <div style={styles.cardSub}>
                        {[src?.city, src?.state, src?.zip].filter(Boolean).join(", ")}
                        {r.externalId ? ` · WO ${r.externalId}` : ""}
                      </div>
                      <div style={styles.radioRow}>
                        {(r.candidates || []).map((c) => (
                          <div key={c.lcpId} style={styles.candidateRow}>
                            <label style={{ ...styles.radioOption, flex: 1, marginBottom: 0 }}>
                              <input
                                type="radio"
                                name={`amb-${r.rowIndex}`}
                                checked={chosen?.action === "create" && chosen.matchedLcpId === c.lcpId}
                                onChange={() => chooseCandidate(r.rowIndex, c)}
                              />
                              <div>
                                {c.locationName || addrText(c.address) || c.lcpId}
                                <div style={styles.radioHint}>
                                  {addrHint(c.address) ? `${addrHint(c.address)} · ` : ""}
                                  {(c.score * 100).toFixed(0)}% match
                                </div>
                              </div>
                            </label>
                            {c.locationId && (
                              <a
                                href={`/locations/${c.locationId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={styles.candidateLink}
                                onClick={(e) => e.stopPropagation()}
                              >
                                View
                              </a>
                            )}
                          </div>
                        ))}
                        <label style={styles.radioOption}>
                          <input
                            type="radio"
                            name={`amb-${r.rowIndex}`}
                            checked={chosen?.action === "skip"}
                            onChange={() => skipRow(r.rowIndex)}
                          />
                          <div>
                            Skip this row
                            <div style={styles.radioHint}>Park it — no work order created this pass.</div>
                          </div>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {buckets.needs_review.length > 0 && (
              <>
                <div style={styles.sectionLabel}>No confident match ({buckets.needs_review.length})</div>
                {buckets.needs_review.map((r) => {
                  const src = rowsByIndex[r.rowIndex];
                  const chosen = decisions[r.rowIndex];
                  const label = pickerLabels[r.rowIndex];
                  return (
                    <div key={r.rowIndex} style={styles.card}>
                      <div style={styles.cardTitle}>{src?.line1 || r.externalId || `Row ${r.rowIndex}`}</div>
                      <div style={styles.cardSub}>
                        {[src?.city, src?.state, src?.zip].filter(Boolean).join(", ")}
                        {r.externalId ? ` · WO ${r.externalId}` : ""}
                      </div>
                      <ManualSitePicker
                        servicePlanId={servicePlanId}
                        chosen={
                          chosen?.action === "create" && label
                            ? { matchedLcpId: chosen.matchedLcpId, label }
                            : undefined
                        }
                        onPick={(lcpId, locationId, lbl) => pickManual(r.rowIndex, lcpId, locationId, lbl)}
                      />
                      <label style={{ ...styles.radioOption, marginTop: 8 }}>
                        <input
                          type="radio"
                          name={`nr-${r.rowIndex}`}
                          checked={chosen?.action === "create_site"}
                          onChange={() => createSiteRow(r.rowIndex)}
                        />
                        <div>
                          Create a new site from this address
                          <div style={styles.radioHint}>
                            We&apos;ll look up the address and add it to this plan, then create the work order.
                          </div>
                        </div>
                      </label>
                      <label style={{ ...styles.radioOption, marginTop: 8 }}>
                        <input
                          type="radio"
                          name={`nr-${r.rowIndex}`}
                          checked={chosen?.action === "skip"}
                          onChange={() => skipRow(r.rowIndex)}
                        />
                        <div>
                          Skip this row
                          <div style={styles.radioHint}>Park it — no work order created this pass.</div>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </>
            )}

            {/* Batch control — a commit-time choice for the whole import,
             * placed AFTER the per-row review. Default "keep" sends no
             * groupId: the backend keeps each site on whatever batch it's
             * already committed to (NOT "no batch"). "existing"/"new" is a
             * deliberate override applied to every row. */}
            {createCount > 0 && (
              <div style={styles.batchBox}>
                <div style={styles.batchTitle}>Batch</div>
                <label style={styles.radioOption}>
                  <input
                    type="radio"
                    name="batch-mode"
                    checked={batchMode === "keep"}
                    onChange={() => setBatchMode("keep")}
                  />
                  <div>
                    Keep each site&apos;s current batch
                    <div style={styles.radioHint}>
                      A site already committed to a batch stays on it; others come in without one.
                    </div>
                  </div>
                </label>
                {batchList.length > 0 && (
                  <label style={styles.radioOption}>
                    <input
                      type="radio"
                      name="batch-mode"
                      checked={batchMode === "existing"}
                      onChange={() => setBatchMode("existing")}
                    />
                    <div style={{ flex: 1 }}>
                      Add all to an existing batch
                      {batchMode === "existing" && (
                        <select
                          value={chosenBatchId}
                          onChange={(e) => setChosenBatchId(e.target.value)}
                          style={styles.batchSelect}
                        >
                          <option value="">Choose a batch…</option>
                          {batchList.map((b: any) => (
                            <option key={b.id} value={b.id}>
                              {b.name || "Untitled batch"}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </label>
                )}
                <label style={styles.radioOption}>
                  <input
                    type="radio"
                    name="batch-mode"
                    checked={batchMode === "new"}
                    onChange={() => setBatchMode("new")}
                  />
                  <div style={{ flex: 1 }}>
                    Create a new batch
                    {batchMode === "new" && (
                      <input
                        type="text"
                        value={newBatchName}
                        onChange={(e) => setNewBatchName(e.target.value)}
                        placeholder="New batch name"
                        style={styles.batchInput}
                      />
                    )}
                  </div>
                </label>
              </div>
            )}

            {geoProgress && (
              <div style={styles.rollup}>
                Locating new-site addresses… {geoProgress.done}/{geoProgress.total}
              </div>
            )}

            {errorMsg && <div style={styles.errText}>{errorMsg}</div>}
          </>
        )}

        {phase === "done" && result && (
          <>
            <div style={styles.resultLine}>
              <strong>{result.createdWorkOrderIds?.length ?? 0}</strong> work order
              {(result.createdWorkOrderIds?.length ?? 0) === 1 ? "" : "s"} created.
            </div>
            {(result.skipped ?? 0) > 0 && (
              <div style={styles.resultLine}>{result.skipped} skipped / parked.</div>
            )}
            {(result.duplicates ?? 0) > 0 && (
              <div style={styles.resultLine}>{result.duplicates} were already imported (duplicates).</div>
            )}
            {result.errors && result.errors.length > 0 && (
              <>
                <div style={{ ...styles.sectionLabel, color: "#B91C1C" }}>
                  {result.errors.length} row{result.errors.length === 1 ? "" : "s"} errored
                </div>
                {result.errors.map((e, i) => (
                  <div key={i} style={styles.errText}>
                    {e.externalId ? `${e.externalId}: ` : ""}
                    {e.reason}
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {phase === "review" ? (
          <>
            <BasicButton
              type="button"
              scheme="secondary"
              outline
              onClick={onCancel}
              styles={{ margin: "0" }}
              keepText
            >
              Cancel
            </BasicButton>
            <BasicButton
              type="button"
              onClick={commit}
              disabled={busy || createCount === 0}
              styles={{ margin: "0" }}
              keepText
            >
              {busy ? "Importing…" : `Create ${createCount} work order${createCount === 1 ? "" : "s"}`}
            </BasicButton>
          </>
        ) : (
          <BasicButton type="button" onClick={onCancel} styles={{ margin: "0" }} keepText>
            {phase === "done" ? "Done" : "Cancel"}
          </BasicButton>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default WorkOrderImportModal;
